import { createSlice } from '@reduxjs/toolkit';
import { Section } from 'src/models/place';
import { TicketType } from 'src/models/ticket';
import { TicketProcess, TicketProcessSelectionSummary } from 'src/models/ticketType';

interface TicketTypeState {
  isLoading: boolean;
  isLoadingQuery: boolean;
  isErrorMessage: string;
  statusCode: number;
  ticketsType: TicketType[];
  ticketsTypePaginated: TicketType[];
  page: number;
  limit: number;
  changeTicketType: null;
  processId: number;
  ticketProcess: TicketProcess | null;
  registeredTickets: any;
  unregisteredTickets: any;
  errorTickets: any[];
  totalRegisteredTickets: number;
  totalUnregisteredTickets: number;
  totalErrorTickets: number;
  quantityRegisteredTickets: number;
  quantityUnregisteredTickets: number;
  selectedComboTicketType: TicketType[];
  sectors: Section[];
  checkingTicketIds: Record<string, boolean>;
  giftingTicketIds: Record<string, boolean>;
  updatingQuantityTicketIds: Record<string, boolean>;
  isMassCheckLoading: boolean;
}

const initialState: TicketTypeState = {
  isLoading: false,
  isLoadingQuery: false,
  statusCode: 201,
  isErrorMessage: '',
  ticketsType: [],
  ticketsTypePaginated: [],
  page: 0,
  limit: 25,
  changeTicketType: null,
  processId: null,
  ticketProcess: null,
  registeredTickets: [],
  unregisteredTickets: [],
  errorTickets: [],
  totalRegisteredTickets: 0,
  totalUnregisteredTickets: 0,
  totalErrorTickets: 0,
  quantityRegisteredTickets: 0,
  quantityUnregisteredTickets: 0,
  selectedComboTicketType: [],
  sectors: [],
  checkingTicketIds: {},
  giftingTicketIds: {},
  updatingQuantityTicketIds: {},
  isMassCheckLoading: false
};

export const ticketTypeSlice = createSlice({
  name: 'ticketType',
  initialState,
  reducers: {
    startLoadingTicketsType: (state) => {
      state.isLoading = true;
    },
    stopLoadingTicketsType: (state) => {
      state.isLoading = false;
    },
    setTicketsType: (state, action) => {
      state.ticketsType = [...action.payload];
      state.isLoading = false;
    },
    setTicketsTypePaginated: (state, action) => {
      state.isLoading = false;
      state.page = action.payload.page;
      state.ticketsTypePaginated = action.payload.ticketsType.data;
    },
    setChangeTicketType: (state, action) => {
      state.changeTicketType = action.payload;
      state.isLoading = false;
    },
    setError: (state, action) => {
      const message = action.payload.message;
      state.statusCode = action.payload.status;
      state.isErrorMessage = message;
      state.isLoading = false;
    },
    setProcessId: (state, action) => {
      state.processId = action.payload;
      state.isLoading = false;
    },
    setTicketProcess: (state, action) => {
      state.ticketProcess = action.payload;
      state.isLoading = false;
    },
    setRegisteredTickets: (state, action) => {
      state.registeredTickets = action.payload;
      state.isLoading = false;
    },
    setUnregisteredTickets: (state, action) => {
      state.unregisteredTickets = action.payload;
      state.isLoading = false;
    },
    setErrorTickets: (state, action) => {
      state.errorTickets = action.payload;
      state.isLoading = false;
    },
    setClearTickets: (state) => {
      state.registeredTickets = [];
      state.unregisteredTickets = [];
      state.errorTickets = [];
      state.totalRegisteredTickets = 0;
      state.totalUnregisteredTickets = 0;
      state.totalErrorTickets = 0;
      state.quantityRegisteredTickets = 0;
      state.quantityUnregisteredTickets = 0;
      state.checkingTicketIds = {};
      state.giftingTicketIds = {};
      state.updatingQuantityTicketIds = {};
      state.isLoading = false;
    },
    setTotalTickets: (state, action) => {
      state.totalRegisteredTickets = action.payload.totalRegisteredTickets;
      state.totalUnregisteredTickets = action.payload.totalUnregisteredTickets;
      if (action.payload.totalErrorTickets != null) {
        state.totalErrorTickets = action.payload.totalErrorTickets;
      }
    },
    setClearErrorMessage: (state) => {
      state.isErrorMessage = '';
    },
    setQuantityTickets: (state, action) => {
      state.quantityRegisteredTickets =
        action.payload.quantityRegisteredTickets;
      state.quantityUnregisteredTickets =
        action.payload.quantityUnregisteredTickets;
    },
    setSelectedComboTicketType: (state, action) => {
      state.selectedComboTicketType = action.payload;
    },
    startLoadingQuery: (state) => {
      state.isLoadingQuery = true;
    },
    stopLoadingQuery: (state) => {
      state.isLoadingQuery = false;
    },
    clearTicketsType: (state) => {
      state.ticketsType = [];
      state.isLoading = false;
    },
    setSectors: (state, action) => {
      state.sectors = action.payload;
    },
    setRowCheckLoading: (
      state,
      action: { payload: { id: string; loading: boolean } }
    ) => {
      if (action.payload.loading) {
        state.checkingTicketIds[action.payload.id] = true;
      } else {
        delete state.checkingTicketIds[action.payload.id];
      }
    },
    setRowGiftLoading: (
      state,
      action: { payload: { id: string; loading: boolean } }
    ) => {
      if (action.payload.loading) {
        state.giftingTicketIds[action.payload.id] = true;
      } else {
        delete state.giftingTicketIds[action.payload.id];
      }
    },
    setRowQuantityLoading: (
      state,
      action: { payload: { id: string; loading: boolean } }
    ) => {
      if (action.payload.loading) {
        state.updatingQuantityTicketIds[action.payload.id] = true;
      } else {
        delete state.updatingQuantityTicketIds[action.payload.id];
      }
    },
    setMassCheckLoading: (state, action: { payload: boolean }) => {
      state.isMassCheckLoading = action.payload;
    },
    updateTicketIsChecked: (
      state,
      action: {
        payload: {
          id: number;
          isChecked: boolean;
          isRegistered: boolean;
          isErrorsTab?: boolean;
          selection?: TicketProcessSelectionSummary;
        };
      }
    ) => {
      const list = action.payload.isErrorsTab
        ? state.errorTickets
        : action.payload.isRegistered
          ? state.registeredTickets
          : state.unregisteredTickets;
      const index = list.findIndex((t) => String(t.id) === String(action.payload.id));
      if (index >= 0) {
        list[index] = { ...list[index], isChecked: action.payload.isChecked };
      }
      if (action.payload.selection && state.ticketProcess) {
        state.ticketProcess.selection = action.payload.selection;
      }
    },
    updateTicketIsGift: (
      state,
      action: {
        payload: {
          id: number;
          isGift: boolean;
          isRegistered: boolean;
          isErrorsTab?: boolean;
        };
      }
    ) => {
      const list = action.payload.isErrorsTab
        ? state.errorTickets
        : action.payload.isRegistered
          ? state.registeredTickets
          : state.unregisteredTickets;
      const index = list.findIndex((t) => String(t.id) === String(action.payload.id));
      if (index >= 0) {
        list[index] = { ...list[index], isGift: action.payload.isGift };
      }
    },
    setTicketQuantity: (
      state,
      action: {
        payload: {
          id: number;
          quantity: number;
          isRegistered: boolean;
          isErrorsTab?: boolean;
          tktMaxQtBuy?: number;
        };
      }
    ) => {
      const list = action.payload.isErrorsTab
        ? state.errorTickets
        : action.payload.isRegistered
          ? state.registeredTickets
          : state.unregisteredTickets;
      const index = list.findIndex((t) => String(t.id) === String(action.payload.id));
      if (index < 0) return;

      const row = list[index];
      const newQuantity = Math.max(1, action.payload.quantity);
      let newError = row.error;
      if (action.payload.tktMaxQtBuy != null) {
        newError =
          newQuantity > action.payload.tktMaxQtBuy
            ? 201
            : row.error === 201
              ? null
              : row.error;
      }
      list[index] = { ...row, quantity: newQuantity, error: newError };
    }
  }
});

export const {
  startLoadingTicketsType,
  setTicketsType,
  setTicketsTypePaginated,
  setError,
  setChangeTicketType,
  setProcessId,
  setTicketProcess,
  setRegisteredTickets,
  setUnregisteredTickets,
  setErrorTickets,
  setClearTickets,
  stopLoadingTicketsType,
  setClearErrorMessage,
  setTotalTickets,
  setQuantityTickets,
  setSelectedComboTicketType,
  startLoadingQuery,
  stopLoadingQuery,
  clearTicketsType,
  setSectors,
  setRowCheckLoading,
  setRowGiftLoading,
  setRowQuantityLoading,
  setMassCheckLoading,
  updateTicketIsChecked,
  updateTicketIsGift,
  setTicketQuantity
} = ticketTypeSlice.actions;
